/**
 * CallLauncher — places a softphone call directly, asking only for the caller-ID when there's a choice.
 *
 * Unlike CallMethodSheet (which first asks "softphone or native dialer?" — a meaningful question on
 * mobile), the desktop entry points always call via the softphone. So this resolves the company's
 * usable voice numbers and:
 *   - exactly one  → dials immediately, no UI
 *   - several      → shows a caller-ID picker
 *   - none         → surfaces an error
 *
 * Usage (parent owns the request; set it to open, clear it in onDone):
 *   const [callRequest, setCallRequest] = useState<CallRequest | null>(null);
 *   <CallLauncher request={callRequest} onDone={() => setCallRequest(null)} />
 */

'use client';

import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Phone, X } from 'lucide-react';
import { apiService, apiErrorMessage } from '@/lib/api';
import { voiceManager } from '@/lib/voiceManager';
import { useVoiceCall } from '@/hooks/useVoiceCall';
import { useNotifications, notificationService } from '@/components/ui/NotificationSystem';
import { formatPhoneNumber, formatVoipNumberLabel } from '@/lib/phoneUtils';
import type { PhoneNumberDTO } from '@/types';

export interface CallRequest {
  /** E.164 destination. */
  toE164: string;
  displayName?: string;
  jobId?: string;
}

interface CallLauncherProps {
  /** The call to place. Non-null starts the flow; null renders nothing. */
  request: CallRequest | null;
  /** Called once the call is placed, abandoned, or failed — clear `request` here. */
  onDone: () => void;
}

export function CallLauncher({ request, onDone }: CallLauncherProps) {
  const { makeCall } = useVoiceCall();
  const { addNotification } = useNotifications();
  /** Non-null only when the user must choose between several usable numbers. */
  const [choices, setChoices] = useState<PhoneNumberDTO[] | null>(null);

  /**
   * Close first, then dial without awaiting: the call UI (widget/overlay) is what reports progress and
   * any failure, so a rejection here needs no second error surface — just don't leave it unhandled.
   */
  function dial(req: CallRequest, callerIdE164: string) {
    onDone();
    void makeCall(req.toE164, callerIdE164, req.displayName, req.jobId)
      .catch(() => { /* surfaced by the call widget */ });
  }

  useEffect(() => {
    if (!request) { setChoices(null); return; }
    let cancelled = false;
    (async () => {
      try {
        const supported = await voiceManager.isSupported();
        if (cancelled) return;
        if (!supported) {
          addNotification(notificationService.error(
            'Softphone unavailable',
            'Calling from the browser requires a supported voice number.',
          ));
          onDone();
          return;
        }
        const [numbers, provider] = await Promise.all([
          apiService.getPhoneNumbers(),
          voiceManager.getActiveProvider(),
        ]);
        if (cancelled) return;
        // A call must dial FROM a number of the active provider (a Telnyx call can't use a Twilio
        // number), so numbers of any other provider aren't usable options.
        const usable = numbers.filter(
          (n) => n.capabilitiesVoice && n.isActive && n.provider.toLowerCase() === provider,
        );

        if (usable.length === 0) {
          addNotification(notificationService.error(
            'Call Failed',
            'No company number is available for this call.',
          ));
          onDone();
          return;
        }
        if (usable.length === 1) {
          dial(request, usable[0].e164Number);
          return;
        }
        setChoices(usable);
      } catch (err: unknown) {
        if (cancelled) return;
        // Only number/permission resolution failures land here — dialing reports itself (see dial()).
        addNotification(notificationService.error(
          'Call Failed',
          apiErrorMessage(err, 'Failed to start the call. Please try again.'),
        ));
        onDone();
      }
    })();
    return () => { cancelled = true; };
    // Only re-run for a new request; the parent keeps `request` referentially stable while open.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [request]);

  // Nothing to render unless the user has to pick a number.
  if (!request || !choices) return null;

  return createPortal(
    <>
      <div className="fixed inset-0 z-[9000] bg-black/40" onClick={onDone} aria-hidden />
      <div
        role="dialog"
        aria-modal
        aria-label="Choose which number to call from"
        className="fixed left-1/2 top-1/2 z-[9001] w-[22rem] max-w-[calc(100vw-2rem)] -translate-x-1/2 -translate-y-1/2 overflow-hidden rounded-2xl bg-white shadow-2xl"
      >
        <div className="flex items-start justify-between border-b border-gray-100 px-4 py-3">
          <div className="min-w-0">
            <p className="text-xs font-medium uppercase tracking-wide text-gray-500">Call from</p>
            <p className="truncate text-sm font-semibold text-gray-900">
              {request.displayName
                ? `${request.displayName} · ${formatPhoneNumber(request.toE164)}`
                : formatPhoneNumber(request.toE164)}
            </p>
          </div>
          <button onClick={onDone} className="rounded-full p-1 text-gray-400 hover:bg-gray-100" aria-label="Cancel">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="max-h-72 overflow-y-auto py-1">
          {choices.map((n) => (
            <button
              key={n.e164Number}
              onClick={() => dial(request, n.e164Number)}
              className="flex w-full items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-gray-50"
            >
              <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-green-500 text-white">
                <Phone className="h-4 w-4" />
              </span>
              <span className="min-w-0 flex-1 truncate text-sm font-medium text-gray-900">
                {formatVoipNumberLabel(n.e164Number, n.displayName)}
              </span>
            </button>
          ))}
        </div>
      </div>
    </>,
    document.body,
  );
}
